import useSWRMutation, { type SWRMutationConfiguration } from 'swr/mutation';
import denApi from '@/lib/api/denApi';
import type {
  PolicyProposalImpactPreview,
  PolicyChangeProposal,
} from '@mls-wallet/shared-types';

type PreviewDraftInput = {
  organizationId: string;
  policyChangeProposals: PolicyChangeProposal[];
};

const fetcher = async (
  _key: string,
  { arg }: { arg: PreviewDraftInput }
): Promise<PolicyProposalImpactPreview> => {
  // Org context comes from x-current-org-id cookie (not URL params)
  const url = `admin/policy/proposals/preview-impact`;
  return await denApi.post<
    PolicyProposalImpactPreview,
    { policyChangeProposals: PolicyChangeProposal[] }
  >(url, { policyChangeProposals: arg.policyChangeProposals });
};

/**
 * Hook to preview the impact of draft policy changes before creating a proposal.
 * Shows which pending transactions would be invalidated.
 *
 * @example
 * const { previewImpact, data, isLoading } = usePreviewPolicyProposalImpactDraft();
 *
 * const result = await previewImpact({
 *   organizationId: 'org-123',
 *   policyChangeProposals: [{ action: 'DELETE', policyId: 'pol-456' }]
 * });
 */
export function usePreviewPolicyProposalImpactDraft(
  swrOptions?: SWRMutationConfiguration<
    PolicyProposalImpactPreview,
    Error,
    string,
    PreviewDraftInput
  >
) {
  const { trigger, data, isMutating, error } = useSWRMutation<
    PolicyProposalImpactPreview,
    Error,
    string,
    PreviewDraftInput
  >('admin/policy/proposals/preview-impact', fetcher, swrOptions);

  return {
    previewImpact: trigger,
    data,
    isLoading: isMutating,
    error,
  };
}
